import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ChevronDown, GripVertical, Plus, X } from 'lucide-react';
import { useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react';
import { AutosizeTextarea } from '../../components/ui/autosize-textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { cn } from '../../lib/cn';

export interface DraftItem {
  id: string;
  text: string;
  checked: boolean;
}

export const newItem = (text = '', checked = false): DraftItem => ({
  id: crypto.randomUUID(),
  text,
  checked,
});

type Caret = 'start' | 'end' | number;

interface ItemRowProps {
  item: DraftItem;
  sortable: boolean;
  registerInput: (id: string, el: HTMLTextAreaElement | null) => void;
  onText: (text: string) => void;
  onChecked: (checked: boolean) => void;
  onRemove: () => void;
  onKeyDown: (event: KeyboardEvent<HTMLTextAreaElement>) => void;
  onPaste: (event: React.ClipboardEvent<HTMLTextAreaElement>) => void;
}

function ItemRow({
  item,
  sortable,
  registerInput,
  onText,
  onChecked,
  onRemove,
  onKeyDown,
  onPaste,
}: ItemRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: item.id,
    disabled: !sortable,
  });

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      className={cn(
        'group/item relative flex items-start gap-2 border-y border-transparent py-1 pr-8 pl-1 focus-within:border-line',
        isDragging && 'z-10 bg-inherit shadow-raised',
      )}
    >
      <button
        ref={setActivatorNodeRef}
        type="button"
        aria-label="Déplacer l'élément"
        className={cn(
          'mt-0.5 -ml-1 flex h-5 w-4 shrink-0 cursor-grab items-center justify-center text-muted opacity-0 group-hover/item:opacity-100 focus-visible:opacity-100',
          !sortable && 'invisible',
        )}
        {...attributes}
        {...listeners}
      >
        <GripVertical size={16} />
      </button>
      <Checkbox
        checked={item.checked}
        onChange={onChecked}
        label={item.text || 'Élément'}
        className="mt-0.5"
      />
      <AutosizeTextarea
        ref={(el) => registerInput(item.id, el)}
        value={item.text}
        onChange={(event) => onText(event.target.value.replace(/\r?\n/g, ' '))}
        onKeyDown={onKeyDown}
        onPaste={onPaste}
        aria-label="Élément de liste"
        maxLength={2000}
        className={cn('text-sm leading-5', item.checked && 'text-muted line-through')}
      />
      <button
        type="button"
        aria-label="Supprimer l'élément"
        onClick={onRemove}
        className="absolute top-0.5 right-1 flex size-6 items-center justify-center rounded-full text-muted opacity-0 group-focus-within/item:opacity-100 group-hover/item:opacity-100 hover:bg-hover hover:text-fg"
      >
        <X size={16} />
      </button>
    </div>
  );
}

/**
 * Éditeur de liste à cocher, au plus près de Keep :
 * Entrée crée un élément, Retour arrière sur un élément vide le supprime,
 * un collage multi-lignes crée plusieurs éléments, glisser-déposer pour réordonner.
 */
export function ChecklistEditor({
  items,
  onChange,
  moveCheckedToBottom,
  autoFocus,
}: {
  items: DraftItem[];
  onChange: (items: DraftItem[]) => void;
  moveCheckedToBottom: boolean;
  autoFocus?: boolean;
}) {
  const inputs = useRef(new Map<string, HTMLTextAreaElement>());
  const newItemInput = useRef<HTMLTextAreaElement>(null);
  const [focusRequest, setFocusRequest] = useState<{ id: string; caret: Caret } | null>(null);
  const [showChecked, setShowChecked] = useState(true);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor),
  );

  useLayoutEffect(() => {
    if (!focusRequest) return;
    const el =
      focusRequest.id === '__new__' ? newItemInput.current : inputs.current.get(focusRequest.id);
    if (el) {
      el.focus();
      const pos =
        focusRequest.caret === 'start'
          ? 0
          : focusRequest.caret === 'end'
            ? el.value.length
            : focusRequest.caret;
      el.setSelectionRange(pos, pos);
    }
    setFocusRequest(null);
  }, [focusRequest]);

  useLayoutEffect(() => {
    if (autoFocus) newItemInput.current?.focus();
  }, [autoFocus]);

  const visibleUnchecked = moveCheckedToBottom ? items.filter((i) => !i.checked) : items;
  const checkedItems = moveCheckedToBottom ? items.filter((i) => i.checked) : [];

  const update = (id: string, patch: Partial<DraftItem>) =>
    onChange(items.map((item) => (item.id === id ? { ...item, ...patch } : item)));

  const remove = (id: string, focusPrevious: boolean) => {
    const order = [...visibleUnchecked, ...checkedItems];
    const index = order.findIndex((i) => i.id === id);
    const previous = order[index - 1] ?? order[index + 1];
    onChange(items.filter((item) => item.id !== id));
    if (focusPrevious && previous) setFocusRequest({ id: previous.id, caret: 'end' });
  };

  const handleKeyDown = (item: DraftItem) => (event: KeyboardEvent<HTMLTextAreaElement>) => {
    const el = event.currentTarget;
    if (event.key === 'Enter' && !event.shiftKey && !event.ctrlKey && !event.metaKey) {
      event.preventDefault();
      // Coupe l'élément au niveau du curseur, comme un traitement de texte.
      const before = item.text.slice(0, el.selectionStart);
      const after = item.text.slice(el.selectionEnd);
      const created = newItem(after, false);
      const index = items.findIndex((i) => i.id === item.id);
      const next = [...items];
      next[index] = { ...item, text: before };
      next.splice(index + 1, 0, created);
      onChange(next);
      setFocusRequest({ id: created.id, caret: 'start' });
    } else if (event.key === 'Backspace' && el.selectionStart === 0 && el.selectionEnd === 0) {
      event.preventDefault();
      const order = [...visibleUnchecked, ...checkedItems];
      const index = order.findIndex((i) => i.id === item.id);
      const previous = order[index - 1];
      if (item.text === '') remove(item.id, true);
      else if (previous && previous.checked === item.checked) {
        // Fusionne avec l'élément précédent.
        const caret = previous.text.length;
        onChange(
          items
            .filter((i) => i.id !== item.id)
            .map((i) => (i.id === previous.id ? { ...i, text: i.text + item.text } : i)),
        );
        setFocusRequest({ id: previous.id, caret });
      }
    } else if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      const order = [...visibleUnchecked, ...(showChecked ? checkedItems : [])];
      const index = order.findIndex((i) => i.id === item.id);
      const target = order[index + (event.key === 'ArrowUp' ? -1 : 1)];
      const atEdge =
        event.key === 'ArrowUp' ? el.selectionStart === 0 : el.selectionEnd === el.value.length;
      if (atEdge && target) {
        event.preventDefault();
        setFocusRequest({ id: target.id, caret: 'end' });
      } else if (atEdge && event.key === 'ArrowDown' && !item.checked) {
        event.preventDefault();
        setFocusRequest({ id: '__new__', caret: 'end' });
      }
    }
  };

  const handlePaste = (item: DraftItem) => (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const text = event.clipboardData.getData('text/plain');
    const lines = text.split(/\r?\n/).filter((l) => l.trim() !== '');
    if (lines.length < 2) return;
    event.preventDefault();
    const el = event.currentTarget;
    const before = item.text.slice(0, el.selectionStart);
    const after = item.text.slice(el.selectionEnd);
    const created = lines.slice(1).map((line) => newItem(line.trim()));
    const last = created.at(-1)!;
    const index = items.findIndex((i) => i.id === item.id);
    const next = [...items];
    next[index] = { ...item, text: before + lines[0]!.trim() };
    created[created.length - 1] = { ...last, text: last.text + after };
    next.splice(index + 1, 0, ...created);
    onChange(next);
    setFocusRequest({ id: last.id, caret: last.text.length });
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const from = items.findIndex((i) => i.id === active.id);
    const to = items.findIndex((i) => i.id === over.id);
    if (from >= 0 && to >= 0) onChange(arrayMove(items, from, to));
  };

  const registerInput = (id: string, el: HTMLTextAreaElement | null) => {
    if (el) inputs.current.set(id, el);
    else inputs.current.delete(id);
  };

  const row = (item: DraftItem, sortable: boolean) => (
    <ItemRow
      key={item.id}
      item={item}
      sortable={sortable}
      registerInput={registerInput}
      onText={(text) => update(item.id, { text })}
      onChecked={(checked) => update(item.id, { checked })}
      onRemove={() => remove(item.id, false)}
      onKeyDown={handleKeyDown(item)}
      onPaste={handlePaste(item)}
    />
  );

  return (
    <div className="flex flex-col">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext
          items={visibleUnchecked.map((i) => i.id)}
          strategy={verticalListSortingStrategy}
        >
          {visibleUnchecked.map((item) => row(item, true))}
        </SortableContext>
      </DndContext>

      {/* Ligne « + Élément de liste » : la saisie crée immédiatement un nouvel élément. */}
      <div className="flex items-start gap-2 border-y border-transparent py-1 pl-4 focus-within:border-line">
        <Plus size={18} className="mt-px shrink-0 text-muted" aria-hidden />
        <AutosizeTextarea
          ref={newItemInput}
          value=""
          placeholder="Élément de liste"
          aria-label="Nouvel élément de liste"
          className="text-sm leading-5"
          onChange={(event) => {
            const created = newItem(event.target.value.replace(/\r?\n/g, ' '));
            onChange(
              moveCheckedToBottom
                ? [...visibleUnchecked, created, ...checkedItems]
                : [...items, created],
            );
            setFocusRequest({ id: created.id, caret: 'end' });
          }}
          onPaste={(event) => {
            const lines = event.clipboardData
              .getData('text/plain')
              .split(/\r?\n/)
              .filter((l) => l.trim());
            if (lines.length === 0) return;
            event.preventDefault();
            const created = lines.map((line) => newItem(line.trim()));
            onChange(
              moveCheckedToBottom
                ? [...visibleUnchecked, ...created, ...checkedItems]
                : [...items, ...created],
            );
            setFocusRequest({ id: created.at(-1)!.id, caret: 'end' });
          }}
        />
      </div>

      {checkedItems.length > 0 && (
        <div className="mt-1 border-t border-line pt-1">
          <button
            type="button"
            onClick={() => setShowChecked((v) => !v)}
            aria-expanded={showChecked}
            className="flex h-8 w-full items-center gap-2 rounded px-1 text-sm text-muted hover:text-fg"
          >
            <ChevronDown
              size={18}
              className={cn('transition-transform', !showChecked && '-rotate-90')}
            />
            {checkedItems.length === 1
              ? '1 élément coché'
              : `${checkedItems.length} éléments cochés`}
          </button>
          {showChecked && checkedItems.map((item) => row(item, false))}
        </div>
      )}
    </div>
  );
}
